import { useCallback, useEffect, useRef, useState } from 'react';
import { PollNotificationsAPI, MarkNotificationsReadAPI, MarkAllNotificationsReadAPI } from '../../api/API';
import { DEMO_DATA_REMOVED_EVENT } from '../../components/demo-data/demoData';

/** Um poll a cada 30 s por aba; a chamada é só um resumo quando nada mudou. */
const POLL_INTERVAL_MS = 30 * 1000;
/** Ao voltar o foco para a aba, não repete uma chamada feita há poucos segundos. */
const FOCUS_THROTTLE_MS = 5 * 1000;
/** Informações que também viram toast: o paciente confirmou ou remarcou pelo link. */
const TOAST_INFO_TYPES = ['appointment_confirmed', 'appointment_rescheduled'];
/** Toasts na fila de uma vez; o resto fica só no sino. */
const MAX_TOAST_QUEUE = 3;

const toastable = (item) => !item.is_read && !item.is_resolved
  && (item.kind === 'action' || TOAST_INFO_TYPES.includes(item.type));

/**
 * Sino do cabeçalho: avisos abertos do usuário logado, quase em tempo real.
 *
 * Um único timer para o app inteiro (o Header é global). Para quando a aba está oculta e
 * busca de novo ao voltar o foco. O servidor devolve a lista só quando algo mudou desde o
 * stamp da chamada anterior; a resposta de rotina é um resumo de uma consulta.
 *
 * Toast: avisos que pedem providência (kind action) e as confirmações e remarcações feitas
 * pelo paciente, que chegaram depois da primeira carga. Confirmações do mesmo dia são uma
 * linha só (dedupe), então uma linha já vista que mudou de updated_at também vira toast.
 * Vários avisos no mesmo poll entram numa fila e aparecem um de cada vez.
 *
 * @returns {{ items, unreadCount, loaded, newItem, dismissNewItem, markRead, markAllRead, refresh }}
 */
const useNotifications = () => {
  const [items, setItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [toastQueue, setToastQueue] = useState([]);

  const stampRef = useRef('');
  const maxSeenIdRef = useRef(null);
  const seenUpdatedAtRef = useRef(new Map());
  const itemsRef = useRef([]);
  itemsRef.current = items;
  const lastPollAtRef = useRef(0);
  const inFlightRef = useRef(false);

  const poll = useCallback(() => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    lastPollAtRef.current = Date.now();

    PollNotificationsAPI(stampRef.current, (response) => {
      inFlightRef.current = false;
      if (!response || !response.success) return;

      setUnreadCount(response.unread_count || 0);
      setLoaded(true);

      if (!response.changed) return;
      stampRef.current = response.stamp || '';
      const list = Array.isArray(response.items) ? response.items : [];
      setItems(list);

      const latestId = response.latest_id || 0;
      if (maxSeenIdRef.current !== null) {
        const seen = seenUpdatedAtRef.current;
        const fresh = list.filter((item) => {
          if (!toastable(item)) return false;
          if (item.id > maxSeenIdRef.current) return true;
          return TOAST_INFO_TYPES.includes(item.type) && seen.has(item.id) && seen.get(item.id) !== item.updated_at;
        });
        if (fresh.length > 0) {
          // A lista vem do mais recente para o mais antigo; o toast mostra na ordem em que chegaram
          const arrived = fresh.slice(0, MAX_TOAST_QUEUE).reverse();
          setToastQueue((queue) => [...queue.filter((queued) => !arrived.some((item) => item.id === queued.id)), ...arrived]
            .slice(-MAX_TOAST_QUEUE));
        }
      }
      seenUpdatedAtRef.current = new Map(list.map((item) => [item.id, item.updated_at]));
      maxSeenIdRef.current = Math.max(latestId, maxSeenIdRef.current || 0);
    });
  }, []);

  useEffect(() => {
    poll();

    const timer = setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      poll();
    }, POLL_INTERVAL_MS);

    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastPollAtRef.current < FOCUS_THROTTLE_MS) return;
      poll();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    // A remoção dos dados de exemplo grava um aviso no sino: busca na hora, sem esperar o próximo poll
    window.addEventListener(DEMO_DATA_REMOVED_EVENT, poll);

    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
      window.removeEventListener(DEMO_DATA_REMOVED_EVENT, poll);
    };
  }, [poll]);

  // Marca na tela na hora, sem esperar a API (o próximo poll confirma o estado do servidor)
  const applyRead = useCallback((predicate) => {
    const now = new Date().toISOString().slice(0, 19).replace('T', ' ');
    const readNow = itemsRef.current.filter((item) => !item.is_read && !item.is_resolved && predicate(item)).length;
    setItems((current) => current.map((item) => (
      item.is_read || !predicate(item) ? item : { ...item, is_read: true, read_at: now }
    )));
    setUnreadCount((count) => Math.max(0, count - readNow));
  }, []);

  const markRead = useCallback((ids) => {
    const wanted = (Array.isArray(ids) ? ids : [ids]).filter(Boolean);
    if (wanted.length === 0) return;
    applyRead((item) => wanted.includes(item.id));
    MarkNotificationsReadAPI(wanted, () => {});
  }, [applyRead]);

  const markAllRead = useCallback(() => {
    applyRead(() => true);
    setUnreadCount(0);
    MarkAllNotificationsReadAPI(() => {});
  }, [applyRead]);

  const dismissNewItem = useCallback(() => setToastQueue((queue) => queue.slice(1)), []);

  return { items, unreadCount, loaded, newItem: toastQueue[0] || null, dismissNewItem, markRead, markAllRead, refresh: poll };
};

export default useNotifications;
